import { useConfigurationBranch } from "@/hooks/useConfigurationBranch"
import { isAdminPortal } from "@/utilities/domain"
import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Grid,
} from "@mui/material"
import { z } from "zod"
import ClearIcon from "@mui/icons-material/Clear"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { useCrudContext } from "@/context/crudContext"

const SupplierSchema = z.object({
  name: z.string().min(3),
  email: z.string().email(),
  phone: z
    .string()
    .length(10, { message: "Phone number should contain 10 digits" }),
  address: z.string(),
  contact_person: z.string().min(1, { message: "Contact person is required" }),
  pan_number: z.preprocess(
    (val) => (val === null || val === "" ? undefined : val),
    z.number().optional()
  ),
})

const AddSupplierModal = ({ open, onClose }) => {
  const { branchId, branchSelector } = useConfigurationBranch()
  const queryClient = useQueryClient()
  const { openDialog, setOpenDialog, selectedRow, setSelectedRow } =
    useCrudContext()

  const { api, register, handleSubmit, errors, isSubmitting, reset, setValue } =
    useCustomForm({
      resolver: zodResolver(SupplierSchema),
    })

  const defaultOnClose = () => {
    setOpenDialog(false)
  }

  // Helper function to set form values
  const setFormValues = (data) => {
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset() // Reset the form if no data is provided
    }
  }

  // Set form values when supplier data is available (for editing)
  useEffect(() => {
    setFormValues(selectedRow)
  }, [selectedRow, setValue, reset])

  const onSubmit = async (data) => {
    const method = selectedRow ? "PUT" : "POST" // Use PUT for updating, POST for adding
    const url = selectedRow ? `supplier/${selectedRow.id}` : "supplier"

    if (data.pan_number) {
      data.pan_number = Number(data.pan_number)
    }

    if (!selectedRow && isAdminPortal()) data.branch_id = branchId
    const response = await api(url, {
      method,
      body: JSON.stringify(data),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["supplier"] })
      toast.success(
        `Supplier successfully ${selectedRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  const handleReset = () => {
    setFormValues(selectedRow)
  }

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          maxWidth: 500,
          margin: "100px auto",
          bgcolor: "white",
          borderRadius: 1,
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: 1,
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
            }}
            className="text-black"
          >
            {selectedRow ? "Update Supplier" : "Add New Supplier"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          {!selectedRow && branchSelector && (
            <Box sx={{ mb: 2 }}>{branchSelector}</Box>
          )}
          <TextField
            fullWidth
            label="Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register("name")}
          />
          <TextField
            fullWidth
            label="Contact Person Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.contact_person}
            helperText={errors.contact_person?.message}
            {...register("contact_person")}
          />

          <TextField
            fullWidth
            label="Email "
            sx={{ marginBottom: 2 }}
            error={!!errors.email}
            helperText={errors.email?.message}
            {...register("email")}
          />

          <TextField
            fullWidth
            label="Phone"
            sx={{ marginBottom: 2 }}
            error={!!errors.phone}
            helperText={errors.phone?.message}
            {...register("phone")}
          />

          <TextField
            fullWidth
            label="Address"
            sx={{ marginBottom: 2 }}
            error={!!errors.address}
            helperText={errors.address?.message}
            {...register("address")}
          />
          <TextField
            fullWidth
            label="PAN Number (optional)"
            sx={{ marginBottom: 2 }}
            error={!!errors.pan_number}
            helperText={errors.pan_number?.message}
            {...register("pan_number")}
          />

          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "10px",
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "11px 30px",
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddSupplierModal
